/* eslint-disable no-undef */
import {
  generatePayloadBase,
  sendPixelEvent,
  eventFactory,
} from "@polar-analytics/pixel-sdk";
import * as Sentry from "@sentry/vue";

const SHOPIFY_SHOP_URL =
  process.env.SHOPIFY_STORE_URL || "rb-redev.myshopify.com";
const PIXEL_ENDPOINT = "https://api-production.polaranalytics.com/pixel";

/**
 * Tracks an Add to Cart event with Polar Analytics.
 *
 * Call this after a successful cart addition via the Storefront API.
 * Polar uses this event for attribution, funnel tracking, and abandoned-cart flows.
 *
 * @param {Object} params
 * @param {string} params.variantId       - Shopify variant GID, e.g. "gid://shopify/ProductVariant/12345"
 * @param {string} params.productId       - Shopify product GID, e.g. "gid://shopify/Product/12345"
 * @param {string} params.productTitle    - Product title
 * @param {string} params.productType     - Product type (e.g. "Dresses")
 * @param {string} params.productUrl      - Product URL
 * @param {string} params.variantTitle    - Variant title (e.g. "UK 8" or "S / Black")
 * @param {string} params.sku             - Variant SKU
 * @param {number} params.unitPrice       - Price per unit as a number (e.g. 180.00)
 * @param {string} params.currencyCode    - ISO currency code (e.g. "GBP")
 * @param {number} params.quantity        - Quantity added
 * @param {string} [params.imageUrl]      - Product/variant image URL (optional)
 * @param {string} [params.vendor]        - Vendor name (optional, defaults to "Rat & Boa")
 */
export const trackPolarAddToCart = async ({
  variantId,
  productId,
  productTitle,
  productType,
  productUrl,
  variantTitle,
  sku,
  unitPrice,
  currencyCode,
  quantity,
  imageUrl,
  vendor = "Rat & Boa",
}) => {
  try {
    // Generate the payload base (captures URL, referrer, UTM params, etc.).
    // The SDK destructures its first argument with no default, so calling it
    // bare throws synchronously and silently drops the event. Pass {} at minimum.
    const payloadBase = await generatePayloadBase({});

    // Calculate total amount for this line item
    const price = parseFloat(unitPrice) || 0;
    const totalAmount = price * quantity;

    // Extract relative URL if full URL is provided
    let relativeUrl = productUrl;
    if (productUrl && productUrl.startsWith("http")) {
      try {
        relativeUrl = new URL(productUrl).pathname;
      } catch (e) {
        relativeUrl = productUrl;
      }
    }

    // Build the event data using Polar's eventFactory
    const data = eventFactory.product_added_to_cart({
      shopifyEventId: crypto.randomUUID(),
      shopifyShopURL: SHOPIFY_SHOP_URL,
      shopifyEventData: {
        cartLine: {
          cost: {
            totalAmount: {
              amount: totalAmount,
              currencyCode: currencyCode,
            },
          },
          merchandise: {
            id: variantId,
            image: imageUrl ? { src: imageUrl } : null,
            price: {
              amount: price,
              currencyCode: currencyCode,
            },
            product: {
              id: productId,
              title: productTitle,
              type: productType || null,
              untranslatedTitle: productTitle,
              url: relativeUrl,
              vendor: vendor,
            },
            sku: sku || null,
            title: variantTitle,
            untranslatedTitle: variantTitle,
          },
          quantity: quantity,
        },
      },
    });

    const bannerStyle =
      "background:#6f42c1;color:#fff;font-size:14px;font-weight:700;padding:4px 10px;border-radius:4px;";

    console.log(
      "%c POLAR ANALYTICS — product_added_to_cart FIRING ",
      bannerStyle,
      {
        endpoint: PIXEL_ENDPOINT,
        shopifyShopURL: SHOPIFY_SHOP_URL,
        shopifyEventId: data.shopifyEventId,
        userId: payloadBase?.data?.user_id,
        sessionId: payloadBase?.data?.session_id,
        productTitle,
        variantTitle,
        quantity,
        unitPrice: price,
        totalAmount,
        currencyCode,
        payloadBase,
        data,
      }
    );

    const result = await sendPixelEvent(PIXEL_ENDPOINT, payloadBase, { data });

    console.log(
      "%c POLAR ANALYTICS — product_added_to_cart SENT ",
      bannerStyle,
      {
        success: result?.success,
        attempts: result?.attempts,
        error: result?.error,
        shopifyEventId: data.shopifyEventId,
      }
    );
  } catch (err) {
    console.warn("[Polar Analytics] Failed to track Add to Cart event:", err);
    Sentry.captureException(err);
  }
};
